import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import type { UserInfo } from "../../shared/model";
import { api } from "./api";
import * as store from "./store";
import { setSyncUser, syncNow } from "./sync";

interface SessionValue {
  /** undefined while checking, null when signed out */
  user: UserInfo | null | undefined;
  offline: boolean;
  setUser: (u: UserInfo | null) => void;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<SessionValue | null>(null);
const CACHE = "ponder.user";

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<UserInfo | null | undefined>(undefined);
  const [offline, setOffline] = useState(false);

  const setUser = useCallback((u: UserInfo | null) => {
    setUserState(u);
    try {
      if (u) localStorage.setItem(CACHE, JSON.stringify(u));
      else localStorage.removeItem(CACHE);
    } catch {
      /* private mode */
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const r = await api.me();
      setOffline(false);
      setUser(r.user);
    } catch {
      // offline: trust the last known account so everything on this device keeps working
      setOffline(true);
      try {
        setUserState(JSON.parse(localStorage.getItem(CACHE) || "null"));
      } catch {
        setUserState(null);
      }
    }
  }, [setUser]);

  const signOut = useCallback(async () => {
    await syncNow(); // best effort: get the latest up to the account first
    setSyncUser(null); // no more syncing from here on
    try {
      await api.logout();
    } finally {
      store.resetToGuest();
      setUser(null);
    }
  }, [setUser]);

  useEffect(() => {
    void refresh();
    const on = () => void refresh();
    window.addEventListener("online", on);
    return () => window.removeEventListener("online", on);
  }, [refresh]);

  // signing in claims this device's progress for the account (or clears another account's leftovers)
  useEffect(() => {
    if (user) {
      store.adoptOwner(user.id, user.name);
      setSyncUser(user.id);
    } else if (user === null) {
      setSyncUser(null);
    }
  }, [user]);

  return <Ctx.Provider value={{ user, offline, setUser, refresh, signOut }}>{children}</Ctx.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSession outside SessionProvider");
  return v;
}
